
import { distinctUntilChanged, filter, map, switchMap, take } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Observable, of, BehaviorSubject, combineLatest } from 'rxjs';
import { PageCriteriaModelInterface } from '../../../../../../Common/Misc/models/page-criteria.model.interface';
import { TargetingSearch } from '../../../../models/targeting-search.model';
import { TargetingSearchesApiService } from '../../../../../../api/targeting/targeting-searches.api.service';
import { TargetingListsApiService } from '../../../../../../api/targeting/targeting-lists.api.service';
import { LocalizedEnum } from '../../../../../../api/journodex/model/localized-enum';
import { FilterTypeModel } from '../../../../../../Common/Filter/models/abstract-filter-type.model';
import { TargetingSearchResponseApiModel } from '../../../../../../api/targeting/model/targeting-search-response.api.model';
import { JournodexSubjectsService } from '../../../../../../Common/Journodex/services/journodex-subjects.service';
import { JournodexEnumsService } from '../../../../../Core/services/journodex-enums.service';
import { LocalizationService } from '../../../../../../Core/Localization/services/localization.service';
import { TargetingSearchService } from '../../../../services/targeting-search.service';
import { OutletsPublicApi } from '../../../../../../api/targeting/outlets-public.api';
import { BaseSearchService, TargetingFacetCollection, FacetValueModel } from '../../../../services/base-search.service';
import { EntityIdApiModel } from '../../../../../../api/targeting/model/entity-id-api-model';
import { RecipientSearchModel } from './recipient-search.model';
import { DiscoverRoutingService } from '../../../../../DiscoverRouter/services/discover-routing.service';
import { DistributionApi } from '../../../../../../api/distributions/distribution.api';
import { RecipientSearchResultsApiModel } from '../../../../../../api/distributions/model/recipient-search-results.api.model';
import { EntityType } from '../../../../../../api/targeting/enum/entity.type.enum';
import { DistributionReportStat } from '../../../../../../Common/TargetingShared/model/statisticsReport/distribution-report-stat-api.model';
import { JournodexRegionsService } from '../../../../../../Common/Journodex/services/journodex-regions.service';
import { SortFieldEnum } from '../../../../../../Common/TargetingShared/enum/SortField.enum';
import { TargetingResultFactory } from '../../../../factories/targeting-result.factory';
import { promiseOnlyResolved } from '../../../../../../Core/utils';
import { TargetingQueryApi } from '../../../../../../api/targeting/model/targeting-search.api.model';
import { UserPreferencesService } from '../../../../../../Account/UserPreferences/services/user-preferences.service';
import {
    IndividualRecipientReportDataModel
} from '../../../../../../Common/TargetingShared/components/statisticReport/RecipientReport/recipient.report.data.model';
import { CurrentUserStateService } from '../../../../../../Core/Users/services/current-user-state.service';
import { TargetingSearchFacetsEnum } from '../../../../../../api/targeting/enum/targeting-search-facets.enum';
import { FilterSectionEnum } from '../../../../../../Common/Filter/enums/filter-section.enum';


@Injectable()
export class RecipientSearchService extends BaseSearchService {

    sortFields: LocalizedEnum[] = [
        {
            enumValue: SortFieldEnum.CIRCULATION_UNIQUE_VISITORS,
            displayString: 'Discover.targeting.sortFields.uniqueVisitors'
        },
        {
            enumValue: SortFieldEnum.FIRST_NAME,
            displayString: 'Discover.targeting.sortFields.firstName'
        },
        {
            enumValue: SortFieldEnum.LAST_NAME,
            displayString: 'Discover.targeting.sortFields.lastName'
        },
        {
            enumValue: SortFieldEnum.JOB_TITLE,
            displayString: 'Discover.targeting.sortFields.jobTitle'
        },
        {
            enumValue: SortFieldEnum.OUTLET_NAME,
            displayString: 'Discover.targeting.sortFields.outletName'
        },
        {
            enumValue: SortFieldEnum.COUNTRY_NAME,
            displayString: 'Common.country'
        },
        {
            enumValue: SortFieldEnum.PROVINCE_NAME,
            displayString: 'Common.province'
        },
        {
            enumValue: SortFieldEnum.CITY_NAME,
            displayString: 'Common.city'
        }
    ];

    recipientSearch: RecipientSearchModel = new RecipientSearchModel();
    distributionId: number;

    protected selectedStatsType$: BehaviorSubject<DistributionReportStat> = new BehaviorSubject<DistributionReportStat>(null);
    protected selectedLink$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

    protected selectedIndividualRecipient: IndividualRecipientReportDataModel;
    protected selectedIndividualRecipient$: BehaviorSubject<IndividualRecipientReportDataModel> = new BehaviorSubject<IndividualRecipientReportDataModel>(null);

    constructor(
        protected targetingSearchesApiService: TargetingSearchesApiService,
        protected targetingListsApiService: TargetingListsApiService,
        protected subjectsService: JournodexSubjectsService,
        protected journodexEnumsService: JournodexEnumsService,
        protected journodexRegionService: JournodexRegionsService,
        protected localizationService: LocalizationService,
        protected targetingSearchService: TargetingSearchService,
        protected outletsApi: OutletsPublicApi,
        protected discoverRoutingService: DiscoverRoutingService,
        protected distributionApi: DistributionApi,
        protected targetingResultFactory: TargetingResultFactory,
        protected userPreferencesService: UserPreferencesService,
        protected currentUserStateService: CurrentUserStateService,
    ) {
        super(
            targetingSearchesApiService,
            targetingListsApiService,
            subjectsService,
            journodexEnumsService,
            journodexRegionService,
            localizationService,
            targetingResultFactory,
            currentUserStateService,
            distributionApi
        );

        this.subscriptions.push(
            combineLatest([
                this.discoverRoutingService.getCurrentRoute(),
                this.userPreferencesService.getUserAppPreferences().pipe(
                    map (userPreference => userPreference.targetingPrefs.savedSearchesPrefs.defaultDisplaySettings)),
                this.getSelectedStatType().pipe(distinctUntilChanged()),
            ]).subscribe(([route, userPreference, statType]) => {
                this.distributionId = +route.value;
                this.pageCriteria.pageSize = userPreference.pageSize;
                this.setPageCriteria(this.pageCriteria, false);
                if (statType && this.distributionId) {
                    this.doSearch(true, true);
                }
            }),
            this.discoverRoutingService.getCurrentRoute().pipe(
                filter(route => !!route?.value),
                take(1)
            ).subscribe(route => {
                const search = TargetingSearch.constructFromRouteParam(route.queryParamMap);
                if (search) {
                    this.populateFiltersFromSearch(search);
                    this.applyFilters();
                }
            })
        );
        this.setFilters(this.allFilters);
        this.targetingSortFields$.next(this.sortFields);
        this.sortField = this.sortFields[0];
        this.sortField$.next(this.sortFields[0]);
    }

    getFilterDefinitions(): FilterTypeModel[] {
        const filters = [
            this.audienceReachFilter,
            this.coverageFocusFilter,
            this.subjectFilter,
            this.productFrequencyFilter,
            this.jobRoleFilter,
            this.jobTitleFilter,
            this.locationFilter,
            this.marketSectorFilter,
            this.mediaTypeFilter,
            this.contactNameFilter,
            this.productTypeFilter,
            this.languageFilter,
            this.statusFilter,
            this.targetAudienceFilter,
            this.targetAudienceRegionFilter,
            this.outletNameFilter,
        ];

        this.outletNameFilter.section = FilterSectionEnum.CONTACT_DETAIL;

        // TODO: add aoc and list filter, alphabetically. Backend not implemented at the moment

        return filters;
    }

    setLink(link: string) {
        this.recipientSearch.statType = undefined;
        this.recipientSearch.link = link;
        this.selectedLink$.next(link);
        this.doSearch(true, true);
    }

    // The top section will pass null as individual recipient
    setStatType(statType: DistributionReportStat, individualRecipient: IndividualRecipientReportDataModel) {
        this.recipientSearch.link = undefined;
        this.recipientSearch.statType = statType;
        this.selectedIndividualRecipient = individualRecipient;
        if (individualRecipient) {
            this.recipientSearch.listId = individualRecipient.listId;
            this.recipientSearch.searchId = individualRecipient.searchId;
        } else {
            delete this.recipientSearch.listId;
            delete this.recipientSearch.searchId;
        }
        this.selectedStatsType$.next(this.recipientSearch.statType);
        this.selectedIndividualRecipient$.next(this.selectedIndividualRecipient);
    }

    getSelectedStatType(): Observable<DistributionReportStat> {
        return this.selectedStatsType$;
    }

    getSelectedLink(): Observable<string> {
        return this.selectedLink$;
    }

    getSelectedIndividualRecipient(): Observable<IndividualRecipientReportDataModel> {
        return this.selectedIndividualRecipient$;
    }

    getItemCount(): Observable<number> {
        return this.nResults$;
    }

    /**
     * Gets the current page criteria
     */
    getPageCriteria(): Observable<PageCriteriaModelInterface> {
        return this.pageCriteria$;
    }

    public setMaxPageCriteria(totalNumberOfPages: number): void {
        this.totalNumberOfPages = totalNumberOfPages;
    }

    /**
     * Updates the page criteria
     */
    setPageCriteria(pageCriteria: PageCriteriaModelInterface, search: boolean = true): void {
        this.page = pageCriteria.page;
        if (this.page <= this.totalNumberOfPages) {
            this.pageCriteria = pageCriteria;
            this.pageCriteria$.next(pageCriteria);
            if (search) {
                this.doSearch();
            }
        }
    }

    /**
     * Checks that the page criteria can be updated
     * @NOTE This is an observable so that users can make the decision via dialogs etc.
     */
    canSetPageCriteria(): Observable<boolean> {
        return of(true);
    }

    public doSearch(refreshPrimaryFacets: boolean = false, refreshFilteredFacets: boolean = false): Observable<TargetingSearchResponseApiModel> {
        if (this.mainSearchRequest) {
            this.mainSearchRequest.unsubscribe();
            this.mainSearchRequest = null;
        }

        const search = new TargetingSearch();
        let filters: TargetingSearch;
        const isFiltered: boolean = this.hasFiltersToApply();

        if (isFiltered) {
            filters = this.generateFiltersSearch();
        }

        const mainSearchRequest: Observable<any> = this.fastSearch(search, filters);

        // just to make sure it runs, in case the caller doesn't subscribe
        this.searchLoading$.next(true);
        this.mainSearchRequest = mainSearchRequest.subscribe({
            next: () => {
                this.mainSearchRequest = null;
                this.fetchListMemberships();
            },
            error: (err) => {
                console.log(err);
            }
        });

        let filteredFacetRequest: Observable<TargetingFacetCollection>;
        if (refreshFilteredFacets) {
            if (this.filteredFacetRequest) {
                this.filteredFacetRequest.unsubscribe();
            }
            filteredFacetRequest = this.facetSearch(search, filters);

            this.filteredFacetRequest = filteredFacetRequest.subscribe(collection => {
                this.setFilteredFacets(collection);
                this.filteredFacetRequest = null;
            });
        }

        let primaryFacetRequest: Observable<TargetingFacetCollection>;
        if (refreshPrimaryFacets) {
            if (this.primaryFacetRequest) {
                this.primaryFacetRequest.unsubscribe();
            }
            if (isFiltered || !filteredFacetRequest) {
                primaryFacetRequest = this.facetSearch(search);
            } else {
                primaryFacetRequest = filteredFacetRequest;
            }
            this.primaryFacetRequest = primaryFacetRequest.subscribe(collection => {
                this.setPrimaryFacets(collection);
                this.primaryFacetRequest = null;
            });
        }

        return mainSearchRequest;
    }

    // Fast search is just the results without any facets
    fastSearch(search: TargetingSearch, filters?: TargetingSearch): Observable<RecipientSearchResultsApiModel> {
        const rows = this.pageCriteria.pageSize;
        const start = (this.pageCriteria.page - 1) * rows;
        const sortOrder = this.sortOrder;
        const sortField = this.sortField ? this.sortField.enumValue : null;

        this.recipientSearch.query = search.toApiModel(false, filters).query;

        const searchRequest: Observable<RecipientSearchResultsApiModel> = this.distributionApi.performRecipientSearch(
            this.distributionId,
            this.recipientSearch,
            start,
            rows,
            sortOrder,
            sortField
        );

        return searchRequest.pipe(
            switchMap(async response => {
                this.nResults = response.numResults;
                this.nResults$.next(this.nResults);
                const resultPromises = response.recipients.map(x => this.targetingResultFactory.createRecipientResult(x));
                this.results = await promiseOnlyResolved(resultPromises);
                return response;
            })
        );
    }

    facetSearch(search: TargetingSearch, filters?: TargetingSearch): Observable<TargetingFacetCollection> {
        const sortOrder = this.sortOrder;
        const sortField = this.sortField ? this.sortField.enumValue : null;

        this.recipientSearch.query = search.toApiModel(true, filters).query;

        const searchRequest: Observable<RecipientSearchResultsApiModel> = this.distributionApi.performRecipientSearch(
            this.distributionId,
            this.recipientSearch,
            0,
            0,
            sortOrder,
            sortField
        );

        return searchRequest.pipe(map(response => {
            return this.parseRecipientFacets(response);
        }));
    }

    private parseRecipientFacets(results: RecipientSearchResultsApiModel): TargetingFacetCollection {
        const facetCollection: TargetingFacetCollection = {
            facets: new Map<TargetingSearchFacetsEnum, FacetValueModel[]>(),
        };

        results.facetResults.forEach(facet => {
            facetCollection.facets.set(facet.facet, facet.counts);
        });

        return facetCollection;
    }

    // TODO : instead of overriding it just to emit loading, make the base version
    // emit a promise
    fetchListMemberships() {

        if (this.results && this.results.length > 0) {
            this.targetingListsApiService.getListMemberships(this.results.map(x => {
                return <EntityIdApiModel> {
                    contactUuid : x.entityType === EntityType.CONTACT ? x.id : null,
                    outletUuid : x.entityType === EntityType.OUTLET ? x.id : null
                };
            })).subscribe(listMemberships => {
                for (let i = 0; i < this.results.length; i++) {
                    this.applyListMembershipsToResult(this.results[i], listMemberships[i]);
                }
                this.results$.next(this.results);
                this.searchLoading$.next(false);
            });
        } else {
            this.results$.next(this.results);
            this.searchLoading$.next(false);
        }
    }

    /**
     * Applies the current filters onto the search
     */
    applyFilters(): void {
        this.setPageNumber(1);

        super.applyFilters();
    }

    setPageNumber(page: number): void {
        this.page = page;
        if (this.pageCriteria) {
            this.pageCriteria.page = page;
            this.pageCriteria$.next(this.pageCriteria);
        }
    }

    getCurrentSearchContext(): TargetingQueryApi {
        let filters: TargetingSearch;
        const isFiltered: boolean = this.hasFiltersToApply();

        if (isFiltered) {
            filters = this.generateFiltersSearch();
        }
        const search = new TargetingSearch();

        return search.toApiModel(false, filters).query;
    }
}
